import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { useAuth } from '../context/AuthContext';
import { Check, Sparkles, Users, Calendar, Brain, Crown, BarChart3, Clock, Bell, Rocket } from 'lucide-react';
import PublicNavbar from '../components/PublicNavbar';

const PricingPage = () => {
  const { user } = useAuth();

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white flex flex-col">
      <PublicNavbar />

      {/* Hero */}
      <div className="flex-1">
        <div className="text-center py-16 px-4">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-purple-100 text-purple-700 rounded-full text-sm font-medium mb-6">
            <Rocket className="h-4 w-4" />
            Coming Soon
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Pro Plans Coming Soon
          </h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto mb-8">
            We're working hard to bring you amazing Pro features. 
            For now, enjoy our free tier with all the essentials!
          </p>
        </div>

        {/* Current Free Plan */}
        <div className="max-w-4xl mx-auto px-4 pb-12">
          <div className="grid md:grid-cols-2 gap-8">
            {/* Free Plan - Current */}
            <div 
              className="relative rounded-3xl p-8 border-2 border-green-500"
              style={{
                background: 'rgba(255, 255, 255, 0.95)',
                backdropFilter: 'blur(40px)',
                boxShadow: '0 4px 24px -4px rgba(0,0,0,0.1)',
              }}
            >
              <div className="absolute -top-3 left-6">
                <span className="px-3 py-1 bg-green-500 text-white text-xs font-bold rounded-full">
                  AVAILABLE NOW
                </span>
              </div>
              
              <div className="mb-6">
                <h3 className="text-2xl font-bold text-gray-900 mb-2">Free</h3>
                <p className="text-gray-500">Everything you need to get started</p>
              </div>
              
              <div className="mb-6">
                <span className="text-5xl font-bold text-gray-900">$0</span>
                <span className="text-gray-500">/month</span>
              </div>

              <Link to={user ? "/dashboard" : "/auth"}>
                <Button className="w-full rounded-full h-12 text-base mb-8 bg-green-600 hover:bg-green-700">
                  {user ? "Go to Dashboard" : "Get Started Free"}
                </Button>
              </Link>

              <div className="space-y-4">
                <p className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Includes:</p>
                
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <div className="w-6 h-6 rounded-full bg-green-100 flex items-center justify-center">
                      <Check className="h-4 w-4 text-green-600" />
                    </div>
                    <span className="text-gray-700">Up to <strong>2 kids</strong></span>
                  </div>
                  
                  <div className="flex items-center gap-3">
                    <div className="w-6 h-6 rounded-full bg-green-100 flex items-center justify-center">
                      <Check className="h-4 w-4 text-green-600" />
                    </div>
                    <span className="text-gray-700">Up to <strong>2 activities</strong> per kid</span>
                  </div>
                  
                  <div className="flex items-center gap-3">
                    <div className="w-6 h-6 rounded-full bg-green-100 flex items-center justify-center">
                      <Check className="h-4 w-4 text-green-600" />
                    </div>
                    <span className="text-gray-700">Up to <strong>5 events</strong> per week</span>
                  </div>
                  
                  <div className="flex items-center gap-3">
                    <div className="w-6 h-6 rounded-full bg-green-100 flex items-center justify-center">
                      <Check className="h-4 w-4 text-green-600" />
                    </div>
                    <span className="text-gray-700">Basic statistics</span>
                  </div>
                  
                  <div className="flex items-center gap-3">
                    <div className="w-6 h-6 rounded-full bg-green-100 flex items-center justify-center">
                      <Check className="h-4 w-4 text-green-600" />
                    </div>
                    <span className="text-gray-700">Family calendar</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Pro Plan - Coming Soon */}
            <div 
              className="relative rounded-3xl p-8 border-2 border-dashed border-purple-300 bg-gradient-to-br from-purple-50 to-indigo-50"
            >
              <div className="absolute -top-3 left-6">
                <span className="px-3 py-1 bg-purple-600 text-white text-xs font-bold rounded-full">
                  COMING SOON
                </span>
              </div>
              
              <div className="mb-6">
                <h3 className="text-2xl font-bold text-gray-900 mb-2 flex items-center gap-2">
                  <Crown className="h-6 w-6 text-purple-600" />
                  Pro
                </h3>
                <p className="text-gray-500">For families who want more</p>
              </div>
              
              <div className="mb-6">
                <span className="text-3xl font-bold text-gray-400">Pricing TBD</span>
              </div>

              <Button 
                disabled
                className="w-full rounded-full h-12 text-base mb-8 bg-gray-300 cursor-not-allowed"
              >
                <Bell className="h-4 w-4 mr-2" />
                Notify Me When Available
              </Button>

              <div className="space-y-4">
                <p className="text-sm font-semibold text-purple-700 uppercase tracking-wide">Will Include:</p>
                
                <div className="space-y-3 opacity-75">
                  <div className="flex items-center gap-3">
                    <div className="w-6 h-6 rounded-full bg-purple-100 flex items-center justify-center">
                      <Users className="h-4 w-4 text-purple-600" />
                    </div>
                    <span className="text-gray-700"><strong>Unlimited</strong> kids</span>
                  </div>
                  
                  <div className="flex items-center gap-3">
                    <div className="w-6 h-6 rounded-full bg-purple-100 flex items-center justify-center">
                      <Calendar className="h-4 w-4 text-purple-600" />
                    </div>
                    <span className="text-gray-700"><strong>Unlimited</strong> activities & events</span>
                  </div>
                  
                  <div className="flex items-center gap-3">
                    <div className="w-6 h-6 rounded-full bg-purple-100 flex items-center justify-center">
                      <Brain className="h-4 w-4 text-purple-600" />
                    </div>
                    <span className="text-gray-700">AI-powered summaries</span>
                  </div>
                  
                  <div className="flex items-center gap-3">
                    <div className="w-6 h-6 rounded-full bg-purple-100 flex items-center justify-center">
                      <Sparkles className="h-4 w-4 text-purple-600" />
                    </div>
                    <span className="text-gray-700">SmartDaddy AI assistant</span>
                  </div>
                  
                  <div className="flex items-center gap-3">
                    <div className="w-6 h-6 rounded-full bg-purple-100 flex items-center justify-center">
                      <BarChart3 className="h-4 w-4 text-purple-600" />
                    </div>
                    <span className="text-gray-700">Advanced statistics</span>
                  </div>
                  
                  <div className="flex items-center gap-3">
                    <div className="w-6 h-6 rounded-full bg-purple-100 flex items-center justify-center">
                      <Clock className="h-4 w-4 text-purple-600" />
                    </div>
                    <span className="text-gray-700">Priority support</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* FAQ Section */}
        <div className="max-w-3xl mx-auto px-4 py-12 border-t border-gray-200">
          <h2 className="text-2xl font-bold text-center text-gray-900 mb-8">Frequently Asked Questions</h2>
          
          <div className="space-y-6">
            <div className="bg-white rounded-xl p-6 shadow-sm">
              <h3 className="font-semibold text-gray-900 mb-2">When will Pro plans be available?</h3>
              <p className="text-gray-600">We're working hard on Pro features and will announce availability soon. Sign up for free to be the first to know!</p>
            </div>
            
            <div className="bg-white rounded-xl p-6 shadow-sm">
              <h3 className="font-semibold text-gray-900 mb-2">What can I do with the free plan?</h3>
              <p className="text-gray-600">The free plan lets you manage up to 2 kids with 2 activities each and 5 events per week. It's perfect for most families getting started!</p>
            </div>
            
            <div className="bg-white rounded-xl p-6 shadow-sm">
              <h3 className="font-semibold text-gray-900 mb-2">Will my data be safe?</h3>
              <p className="text-gray-600">Absolutely! Your family's data is encrypted and securely stored. We take privacy seriously.</p>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="border-t border-gray-200 py-8 px-4">
        <div className="max-w-6xl mx-auto text-center text-gray-500 text-sm">
          <p>© 2026 SmartDaddy AI. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default PricingPage;
